import { useEffect, useState, useCallback } from "react";
import {
  Box, Paper, Stack, Typography, TextField, MenuItem, Chip, Button, Divider,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Swal from "sweetalert2";
import { listarEventos, listarEncontros, participantesEncontro, marcarPresenca } from "../../services/eventoService";

const fmt = (d) => (d ? new Date(d).toLocaleDateString("pt-BR") : "");

export default function PresencaList() {
  const [eventos, setEventos] = useState([]);
  const [idEvento, setIdEvento] = useState("");
  const [encontros, setEncontros] = useState([]);
  const [idEncontro, setIdEncontro] = useState("");
  const [participantes, setParticipantes] = useState([]);
  const [termo, setTermo] = useState("");
  const [salvando, setSalvando] = useState(null);
  const [carregando, setCarregando] = useState(false);

  useEffect(() => { listarEventos().then((r) => setEventos(r.data)).catch(() => {}); }, []);

  // Ao trocar de evento, carrega os encontros dele.
  useEffect(() => {
    setEncontros([]); setIdEncontro(""); setParticipantes([]);
    if (!idEvento) return;
    listarEncontros(idEvento).then((r) => {
      setEncontros(r.data);
      if (r.data.length === 1) setIdEncontro(String(r.data[0].Id));
    }).catch(() => {});
  }, [idEvento]);

  const carregar = useCallback(async () => {
    if (!idEncontro) { setParticipantes([]); return; }
    setCarregando(true);
    try { setParticipantes((await participantesEncontro(idEncontro, termo)).data.Participantes); }
    catch (e) { Swal.fire("Erro", e?.response?.data || e.message, "error"); }
    finally { setCarregando(false); }
  }, [idEncontro, termo]);

  useEffect(() => { carregar(); /* eslint-disable-next-line */ }, [idEncontro]);

  const alternar = async (p) => {
    const marcar = !p.Presente;
    setSalvando(p.Id);
    try {
      await marcarPresenca(p.Id, Number(idEncontro), marcar);
      setParticipantes((arr) => arr.map((x) => (x.Id === p.Id ? { ...x, Presente: marcar } : x)));
    } catch (e) { Swal.fire("Erro", e?.response?.data?.mensagem || e.message, "error"); }
    finally { setSalvando(null); }
  };

  const presentes = participantes.filter((p) => p.Presente).length;

  return (
    <Box>
      <Typography variant="h5" mb={2}>Presença nos encontros</Typography>

      <Stack direction="row" spacing={1} mb={2} flexWrap="wrap" useFlexGap>
        <TextField select size="small" label="Evento" value={idEvento} onChange={(e) => setIdEvento(e.target.value)} sx={{ minWidth: 300 }}>
          <MenuItem value="">Selecione…</MenuItem>
          {eventos.map((e) => <MenuItem key={e.Id} value={e.Id}>{e.Nome}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Encontro" value={idEncontro} onChange={(e) => setIdEncontro(e.target.value)} sx={{ minWidth: 260 }} disabled={!encontros.length}>
          <MenuItem value="">{encontros.length ? "Selecione…" : "— sem encontros —"}</MenuItem>
          {encontros.map((en) => <MenuItem key={en.Id} value={en.Id}>{en.Nome}{en.Data ? ` (${fmt(en.Data)})` : ""}</MenuItem>)}
        </TextField>
        <TextField size="small" label="Buscar (nome, CPF, aluno)" value={termo}
          onChange={(e) => setTermo(e.target.value)} onKeyDown={(e) => e.key === "Enter" && carregar()} sx={{ minWidth: 280 }} disabled={!idEncontro} />
        <Button variant="outlined" onClick={carregar} disabled={!idEncontro}>Buscar</Button>
      </Stack>

      {!idEvento ? (
        <Typography color="text.secondary">Selecione um evento para registrar a presença.</Typography>
      ) : !encontros.length ? (
        <Typography color="text.secondary">Este evento não tem encontros cadastrados. Adicione-os no cadastro do evento.</Typography>
      ) : !idEncontro ? (
        <Typography color="text.secondary">Selecione o encontro.</Typography>
      ) : (
        <>
          <Stack direction="row" spacing={2} mb={1}>
            <Chip label={`${participantes.length} participante(s)`} />
            <Chip color="success" variant="outlined" label={`Presentes: ${presentes} de ${participantes.length}`} />
          </Stack>
          <Paper>
            <TableContainer sx={{ maxHeight: "65vh" }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Participante</TableCell>
                    <TableCell>CPF</TableCell>
                    <TableCell>Aluno</TableCell>
                    <TableCell>Oficinas</TableCell>
                    <TableCell align="center">Presença</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {participantes.map((p) => (
                    <TableRow key={p.Id} hover>
                      <TableCell>{p.Nome}</TableCell>
                      <TableCell>{p.Cpf}</TableCell>
                      <TableCell>{p.Aluno || "—"}</TableCell>
                      <TableCell>{p.Oficinas || "—"}</TableCell>
                      <TableCell align="center">
                        {p.Presente ? (
                          <Button size="small" color="success" variant="contained" disableElevation
                            startIcon={<CheckCircleIcon />} disabled={salvando === p.Id}
                            onClick={() => alternar(p)} sx={{ textTransform: "none", minWidth: 118 }}>
                            Presente
                          </Button>
                        ) : (
                          <Button size="small" variant="outlined" disabled={salvando === p.Id}
                            onClick={() => alternar(p)} sx={{ textTransform: "none", minWidth: 118 }}>
                            {salvando === p.Id ? "…" : "Marcar presença"}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {!participantes.length && (
                    <TableRow><TableCell colSpan={5} align="center">
                      {carregando ? "Carregando…" : "Nenhum participante."}
                    </TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}
    </Box>
  );
}
